import { useGetFreshdeskTicketsQuery } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { Spinner } from "@common";
import {
  Box,
  Card,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { format } from "date-fns";

type Props = {
  consultantId: string;
  isActive?: boolean;
};

const formatDate = (value?: string | number | Date | null) =>
  value ? format(new Date(value), "yyyy-MM-dd") : "—";

export const FreshdeskTicketsTab = (props: Props) => {
  const snack = useSnackbarsPipeline();

  const { data, loading } = useGetFreshdeskTicketsQuery({
    variables: { consultantId: props.consultantId },
    fetchPolicy: "network-only",
    skip: !props.isActive,
    onError: () => snack.showError(locale.ErrorOccurred)
  });

  const tickets = data?.freshdeskTicketsByConsultant ?? [];

  const content = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <Spinner size={32} inline />
        </Box>
      );
    }

    if (tickets.length === 0) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <Typography variant="body2" color="textSecondary">
            {locale.freshdeskTickets.empty}
          </Typography>
        </Box>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{locale.freshdeskTickets.subject}</TableCell>
            <TableCell width="8rem">{locale.freshdeskTickets.status}</TableCell>
            <TableCell width="8rem">{locale.freshdeskTickets.created}</TableCell>
            <TableCell width="8rem">{locale.freshdeskTickets.updated}</TableCell>
            <TableCell width="3rem" />
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.map((ticket) => (
            <TableRow key={ticket.id}>
              <TableCell>{ticket.subject}</TableCell>
              <TableCell>{ticket.status}</TableCell>
              <TableCell>{formatDate(ticket.createdAt)}</TableCell>
              <TableCell>{formatDate(ticket.updatedAt)}</TableCell>
              <TableCell>
                <Link
                  href={ticket.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Typography variant="h6">{locale.freshdeskTickets.title}</Typography>
      {content()}
    </Card>
  );
};

export default FreshdeskTicketsTab;
